import { Component, computed, inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { firstValueFrom } from 'rxjs';
import { AuthenticationService } from '../../authentication/authentication.service';
import { MceApiService } from '../../core/mceapi.client.generated';
import { hasRole, Role } from '../../shared/enums/role';
import { DialogService } from '../../shared/dialogs/dialog.service';
import { InputDialogType } from '../../shared/dialogs/input/input.component';
import { InfoMessageComponent } from '../../shared/components/info-message/info-message.component';

interface ConsumptionMetric {
  key: string;
  label: string;
  icon: string;
  usedLabel: string;
  limitLabel: string;
  remainingLabel: string;
  percentComplete: number;
}

@Component({
  selector: 'app-balance',
  imports: [MatButtonModule, MatCardModule, MatIconModule, MatProgressBarModule, InfoMessageComponent],
  templateUrl: './balance.component.html',
  styles: ``,
})
export class BalanceComponent {

  private readonly authenticationService = inject(AuthenticationService);
  private readonly dialogService = inject(DialogService);
  private readonly mceApiService = inject(MceApiService);

  protected readonly pricingCreditsUrl = 'https://mycableengineering.com/pricing-credits/';

  private readonly accountCreditResource = rxResource({
    stream: () => this.mceApiService.v1SubscriptionsBalance(),
  });

  protected readonly currentPlanName = computed(() => {
    const roles = this.authenticationService.currentUserRoles();

    if (hasRole(roles, Role.ProTier)) return 'Pro';
    if (hasRole(roles, Role.FreeTier)) return 'Free';

    return 'No';
  });

  protected readonly accountCreditBadge = computed(() => {
    const balance = this.accountCreditResource.value()?.balance ?? 0;

    return balance < 0 ? `Account credit ${this.formatCurrency(Math.abs(balance))}` : undefined;
  });

  protected readonly viewState = computed(() => {
    const subscription = this.authenticationService.currentUserSubscription();
    const currentPlanName = this.currentPlanName();
    const isFreePlan = currentPlanName === 'Free';
    const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd?.value ?? false;
    const currentPeriodEnd = this.formatDate(subscription?.currentPeriodEndUTC?.value);
    const creditAllowance = subscription?.creditAllowance ?? 0;
    const creditConsumed = subscription?.creditConsumed ?? 0;
    const paygBudgetLimitGbp = subscription?.paygBudget ?? 0;
    const paygConsumedGbp = subscription?.paygBudgetConsumed ?? 0;
    const showBudgetMessage = paygBudgetLimitGbp < 0.1 && !isFreePlan;

    return {
      creditAllowance,
      creditConsumed,
      paygBudgetLimitGbp,
      paygConsumedGbp,
      isFreePlan,
      showBudgetMessage,

      subscriptionStatusMessage: currentPeriodEnd
        ? cancelAtPeriodEnd && !isFreePlan
          ? `Your ${currentPlanName} plan ends on ${currentPeriodEnd}. Your account will then move to Free.`
          : isFreePlan
            ? `Credits reset on ${currentPeriodEnd}.`
            : `Usage limits reset on ${currentPeriodEnd}.`
        : undefined,
      consumptionMetrics: [
        {
          key: 'credits',
          label: 'Credit consumption',
          icon: 'data_usage',
          usedLabel: `${creditConsumed} credits used`,
          limitLabel: `${creditAllowance} credits included`,
          remainingLabel: `${Math.max(creditAllowance - creditConsumed, 0)} credits remaining`,
          percentComplete: this.percentage(creditConsumed, creditAllowance),
        },
        {
          key: 'payg',
          label: 'PAYG consumption',
          icon: 'data_usage',
          usedLabel: `${this.formatCurrency(paygConsumedGbp)} used`,
          limitLabel: `${this.formatCurrency(paygBudgetLimitGbp)} budget limit`,
          remainingLabel: `${this.formatCurrency(Math.max(paygBudgetLimitGbp - paygConsumedGbp, 0))} remaining`,
          percentComplete: this.percentage(paygConsumedGbp, paygBudgetLimitGbp),
        },
      ] satisfies ConsumptionMetric[],
    };
  });

  protected async setPaygBudget() {
    const currentBudget = this.authenticationService.currentUserSubscription()?.paygBudget ?? 0;
    const requestedLimitGbp = await firstValueFrom(this.dialogService.input(
      InputDialogType.number,
      'PAYG budget limit',
      'Monthly limit in GBP',
      undefined,
      0,
      'Set the maximum GBP budget per billing cycle. You will only be charged for what you actually use.',
      currentBudget,
    ));

    if (typeof requestedLimitGbp !== 'number') return;

    try {
      await firstValueFrom(this.mceApiService.v1BillingBudgetSet({ paygBudget: requestedLimitGbp }));
    } catch (error) {
      console.error('Error updating PAYG budget limit', error);
      throw error;
    }
  }

  protected viewCreditCosts() {
    this.dialogService.creditCosts().subscribe();
  }

  private percentage(used: number, limit: number) {
    if (limit <= 0) {
      return used > 0 ? 100 : 0;
    }

    return Math.min(Math.round((used / limit) * 100), 100);
  }

  private formatCurrency(value: number) {
    return `£${value.toFixed(2)}`;
  }

  private formatDate(value: string | undefined) {
    if (!value) return undefined;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;

    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

}
